import { ITemplateFormAnswers } from '@core/entities/template/TemplateFormAnswers/ITemplateFormAnswers'
import { Entity } from '@core/entities/entity'
import { plainToInstance, Type } from 'class-transformer'
import {
  ITemplateQuestionGroup,
  TemplateQuestionGroup,
} from '@core/entities/template/TemplateQuestionGroup'
import { User } from '@core/entities/user'
import { SubmissionProof } from '@core/entities/template/TemplateQuestion'
import { ChecklistInspectionStats } from '@core/entities/checklist/Checklist/types'
import { ITaskPayload } from '@core/entities/InspectionItemAnswer/ITaskPayload'

export class TemplateFormAnswers extends Entity<ITemplateFormAnswers> {
  @Type(() => TemplateQuestionGroup)
  answers: Array<TemplateQuestionGroup>

  @Type(() => User)
  submittedBy: User
  submittedOn: string

  trackingId?: string

  public static new(payload: unknown): TemplateFormAnswers {
    const entity = plainToInstance(TemplateFormAnswers, payload)

    return entity
  }

  public isAnswered(): boolean {
    return !!this.answers.length && !!this.submittedOn
  }

  public getImageS3Keys(): string[] {
    return this.answers
      ?.flatMap((group) =>
        group.data?.flatMap((question) =>
          question?.submissionProofs?.flatMap((proof) => proof.key),
        ),
      )
      .filter((key) => !!key)
  }

  public getProofs(): SubmissionProof[] {
    return this.answers
      ?.flatMap((group) =>
        group.data?.flatMap((question) => question?.submissionProofs),
      )
      .filter((key) => !!key)
  }

  public getAnswersFormatted(): Array<ITemplateQuestionGroup> {
    return (this.toPlain().answers ?? []).map((questionGroup) => ({
      ...questionGroup,
      data: questionGroup.data.map((question) => {
        if (
          question.questionType === 'INSPECTION_ITEM' &&
          question.inspectionItemAnswers?.length > 0
        ) {
          return {
            ...question,
            inspectionItemAnswers: question.inspectionItemAnswers.map(
              (answer) => {
                if (answer.taskPayloadResult && !answer.taskPayload) {
                  return {
                    ...answer,
                    taskPayload: {
                      ...answer.taskPayloadResult,
                      taskTemplateId: answer.taskPayloadResult.taskTemplate.id,
                      taskTemplateVersionId:
                        answer.taskPayloadResult.taskTemplateVersionResult.id,
                      assigneeUserId: answer.taskPayloadResult.assigneeUser?.id,
                      attachments: {
                        data: answer.taskPayloadResult.attachments?.data ?? [],
                      },
                    },
                  }
                }
                return answer
              },
            ),
          }
        }

        if (question.questionType === 'LINK_REGISTER_ITEMS') {
          return {
            ...question,
            linkedRegisterTemplateId: question.linkedRegisterTemplate?.id,
            previewPropertyIds:
              question.previewProperties?.map(({ id }) => id) || [],
            pinnedFilterIds: question.pinnedFilters?.map(({ id }) => id) || [],
            registerItemIds: question.registerItems?.map(({ id }) => id) || [],
            registerItems: question.registerItems?.map((item) => ({
              ...item,
              register: null,
            })),
          }
        }

        if (question.questionType === 'LINK_PERMITS') {
          return {
            ...question,
            linkedPermitsIds: question.linkedPermitsResults?.map(
              (linkedPermitResult) => linkedPermitResult.id,
            ),
          }
        }

        if (question.questionType === 'LINK_CHECKLISTS') {
          return {
            ...question,
            linkedChecklistsIds: question.linkedChecklistsResults?.map(
              (linkedChecklistResult) => linkedChecklistResult.id,
            ),
          }
        }

        if (question.questionType === 'LINK_INSPECTIONS') {
          return {
            ...question,
            linkedInspectionsIds: question.linkedInspectionsResults?.map(
              (linkedInspectionResult) => linkedInspectionResult.id,
            ),
          }
        }
        return question
      }),
    }))
  }

  public static getRaisedTasks(
    answers: ITemplateQuestionGroup[],
  ): ITaskPayload[] {
    const tasks: ITaskPayload[] = []
    answers.forEach((answer) => {
      answer.data.forEach((question) => {
        if (question.questionType === 'INSPECTION_ITEM') {
          question.inspectionItemAnswers?.forEach((answer) => {
            if (answer.taskPayload) {
              tasks.push(answer.taskPayload)
            }
          })
        }
      })
    })
    return tasks
  }

  public static getInspectionStats(
    answers: ITemplateQuestionGroup[],
  ): ChecklistInspectionStats {
    const stats = answers.reduce(
      (acc, answer) => {
        answer.data.forEach((question) => {
          if (question.questionType === 'INSPECTION_ITEM') {
            question.inspectionItemAnswers?.forEach((itemAnswer) => {
              acc.inspectionItems.total++
              if (itemAnswer.inspectionItemAnswerType === 'YES') {
                acc.inspectionItems.byOutcome.pass++
              } else if (itemAnswer.inspectionItemAnswerType === 'NO') {
                acc.inspectionItems.byOutcome.fail++
              } else if (itemAnswer.inspectionItemAnswerType === 'NA') {
                acc.inspectionItems.byOutcome.na++
              }
              if (itemAnswer.taskPayload) {
                acc.raisedTasks.total++
                const priority = itemAnswer.taskPayload.priority
                if (priority === 'LOW') {
                  acc.raisedTasks.byPriority.low++
                } else if (priority === 'MEDIUM') {
                  acc.raisedTasks.byPriority.medium++
                } else if (priority === 'HIGH') {
                  acc.raisedTasks.byPriority.high++
                } else if (priority === 'CRITICAL') {
                  acc.raisedTasks.byPriority.critical++
                }
              }
            })
          }
        })
        return acc
      },
      {
        inspectionItems: {
          byOutcome: {
            pass: 0,
            fail: 0,
            na: 0,
          },
          total: 0,
        },
        raisedTasks: {
          byPriority: {
            critical: 0,
            high: 0,
            medium: 0,
            low: 0,
          },
          total: 0,
        },
      },
    )

    return stats
  }
}
